"use client";

import * as React from "react";
import Link from "next/link";
import {
  Volume2, VolumeX, Maximize2, Minimize2, Dices, Trophy, Star,
  RotateCcw, Check, X, Sparkles,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { MascotAny } from "@/components/marketing/mascot-any";
import { oceanSound } from "./ocean-sound";
import { ASSET, ANSWERS, ORX_CSS } from "./ocean-stage";
import { FitText } from "./fit-text";
import { Confetti } from "./ocean-confetti";
import type { PlayQuestionDTO } from "@/types/quiz";

type DemoStudent = { id: string; fullName: string; mascot: string };
type Outcome = "saved" | "polluted";
type Sub = "pick" | "choose" | "question";

/** Lớp mẫu 10 học sinh (mascot dựng sẵn) — chỉ dùng cho bản chơi thử công khai. */
const DEMO_STUDENTS: DemoStudent[] = [
  { id: "s1", fullName: "Bảo An", mascot: "sprout" },
  { id: "s2", fullName: "Gia Bình", mascot: "soccer" },
  { id: "s3", fullName: "Linh Chi", mascot: "scientist" },
  { id: "s4", fullName: "Tiến Dũng", mascot: "painter" },
  { id: "s5", fullName: "Thu Hà", mascot: "music" },
  { id: "s6", fullName: "Đăng Khôi", mascot: "student" },
  { id: "s7", fullName: "Ngọc Lan", mascot: "star" },
  { id: "s8", fullName: "Quang Minh", mascot: "pencil" },
  { id: "s9", fullName: "Hoài Nam", mascot: "book" },
  { id: "s10", fullName: "Tường Vy", mascot: "heart" },
];

/** 3 câu hỏi mẫu về chủ đề bảo vệ đại dương. */
const DEMO_QUESTIONS: PlayQuestionDTO[] = [
  {
    id: "q1",
    prompt: "Chai nhựa đã dùng nên được bỏ vào đâu?",
    options: ["Thùng tái chế", "Xuống biển", "Trên bãi cát", "Dưới gốc cây"],
    correctIndex: 0,
  },
  {
    id: "q2",
    prompt: "Việc nào giúp bảo vệ đại dương xanh sạch?",
    options: ["Xả rác xuống biển", "Nhặt rác ở bờ biển", "Đổ dầu ra biển", "Bắt hết cá"],
    correctIndex: 1,
  },
  {
    id: "q3",
    prompt: "Con vật nào sinh sống ở biển?",
    options: ["Cá heo", "Con gà", "Con mèo", "Con trâu"],
    correctIndex: 0,
  },
];

const TOTAL = DEMO_QUESTIONS.length;

/**
 * Bản CHƠI THỬ công khai của "Giải cứu đại dương" — không đăng nhập, không gọi API.
 * Tái hiện chế độ chơi trên lớp (cá nhân): chọn ngẫu nhiên 1 bạn → chọn rác → trả
 * lời → xếp hạng. Dữ liệu (lớp 10 HS + 3 câu) cố định sẵn để demo cách chơi.
 */
export function OceanDemo() {
  const stageRef = React.useRef<HTMLDivElement>(null);
  const [muted, setMuted] = React.useState(false);
  const [isFull, setIsFull] = React.useState(false);

  const [phase, setPhase] = React.useState<"intro" | "playing" | "end">("intro");
  const [sub, setSub] = React.useState<Sub>("pick");
  const [scores, setScores] = React.useState<Record<string, number>>({});
  const [outcomes, setOutcomes] = React.useState<Record<number, Outcome>>({});
  const [current, setCurrent] = React.useState<DemoStudent | null>(null);
  const [highlightId, setHighlightId] = React.useState<string | null>(null);
  const [picking, setPicking] = React.useState(false);
  const [qIndex, setQIndex] = React.useState<number | null>(null);
  const [chosen, setChosen] = React.useState<number | null>(null);
  const [participated, setParticipated] = React.useState<string[]>([]);
  const [celebrate, setCelebrate] = React.useState(false);

  React.useEffect(() => () => oceanSound.stopAmbient(), []);
  React.useEffect(() => {
    const onFs = () => setIsFull(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  const answeredCount = Object.keys(outcomes).length;
  const highlightStudent = DEMO_STUDENTS.find((s) => s.id === highlightId) ?? null;

  function toggleMute() {
    const m = oceanSound.toggle();
    setMuted(m);
    if (!m && phase === "playing") oceanSound.startAmbient();
  }
  async function toggleFullscreen() {
    const el = stageRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await el.requestFullscreen();
    } catch {
      /* bỏ qua */
    }
  }

  function start() {
    oceanSound.unlock();
    if (!muted) oceanSound.startAmbient();
    setOutcomes({});
    setScores(Object.fromEntries(DEMO_STUDENTS.map((s) => [s.id, 0])));
    setParticipated([]);
    setCurrent(null);
    setHighlightId(null);
    setQIndex(null);
    setChosen(null);
    setSub("pick");
    setPhase("playing");
  }

  function drawStudent() {
    if (picking) return;
    oceanSound.play("click");
    setSub("pick");
    setCurrent(null);
    setPicking(true);
    let ticks = 0;
    const spin = setInterval(() => {
      setHighlightId(DEMO_STUDENTS[Math.floor(Math.random() * DEMO_STUDENTS.length)]!.id);
      ticks++;
      if (ticks > 13) {
        clearInterval(spin);
        const s = DEMO_STUDENTS[Math.floor(Math.random() * DEMO_STUDENTS.length)]!;
        setCurrent(s);
        setHighlightId(s.id);
        setPicking(false);
        oceanSound.play("collect");
        setTimeout(() => setSub("choose"), 2000);
      }
    }, 90);
  }

  function chooseTrash(i: number) {
    if (outcomes[i] !== undefined) return;
    oceanSound.play("click");
    setQIndex(i);
    setChosen(null);
    setSub("question");
  }

  function mark(i: number) {
    if (chosen !== null || qIndex === null) return;
    const q = DEMO_QUESTIONS[qIndex];
    if (!q) return;
    const correct = i === q.correctIndex;
    setChosen(i);
    if (correct) {
      setCelebrate(true);
      oceanSound.play("cheer");
      setTimeout(() => oceanSound.play("collect"), 320);
      if (current) setScores((s) => ({ ...s, [current.id]: (s[current.id] ?? 0) + 1 }));
    } else {
      oceanSound.play("wrong");
    }
    if (current) setParticipated((p) => (p.includes(current.id) ? p : [...p, current.id]));
    setTimeout(() => {
      setCelebrate(false);
      setOutcomes((o) => ({ ...o, [qIndex]: correct ? "saved" : "polluted" }));
      setChosen(null);
      setQIndex(null);
      if (answeredCount + 1 >= TOTAL) {
        oceanSound.play("finish");
        oceanSound.stopAmbient();
        setPhase("end");
        return;
      }
      setCurrent(null);
      setHighlightId(null);
      setSub("pick");
    }, 1500);
  }

  const turnLabel = current?.fullName ?? "";
  const rows = participated
    .map((id) => DEMO_STUDENTS.find((s) => s.id === id))
    .filter((s): s is DemoStudent => Boolean(s))
    .map((s) => ({ id: s.id, label: s.fullName, mascot: s.mascot, score: scores[s.id] ?? 0 }))
    .sort((a, b) => b.score - a.score);

  const outerCls = isFull ? "grid h-screen w-screen place-items-center bg-[#04263f]" : "w-full";
  const bg = phase === "intro" ? `${ASSET}/bg-dirty.png` : `${ASSET}/bg-clean.png`;

  return (
    <div ref={stageRef} className={cn("orx select-none", outerCls)}>
      <style>{ORX_CSS}</style>
      <div
        className={cn(
          "orx-frame relative overflow-hidden shadow-lg",
          isFull ? "aspect-[16/9] h-full max-h-full w-auto max-w-full" : "aspect-[16/9] w-full rounded-2xl"
        )}
      >
        <img src={bg} alt="" className="absolute inset-0 h-full w-full object-cover" aria-hidden />

        <div className="absolute right-[1.5%] top-[2.5%] z-40 flex gap-2">
          <button type="button" onClick={toggleMute} aria-label={muted ? "Bật âm thanh" : "Tắt âm thanh"} className="orx-ctrl">
            {muted ? <VolumeX className="size-full" aria-hidden /> : <Volume2 className="size-full" aria-hidden />}
          </button>
          <button type="button" onClick={toggleFullscreen} aria-label="Toàn màn hình" className="orx-ctrl">
            {isFull ? <Minimize2 className="size-full" aria-hidden /> : <Maximize2 className="size-full" aria-hidden />}
          </button>
        </div>

        {celebrate && (
          <>
            <Confetti />
            <div className="pointer-events-none absolute left-1/2 top-[16%] z-[55] -translate-x-1/2">
              <span className="orx-pop inline-block rounded-full bg-emerald-500/95 text-white shadow-lg" style={{ fontSize: "3.4cqw", fontWeight: 800, padding: "1.4cqh 4cqw" }}>
                🎉 Chính xác! 🎉
              </span>
            </div>
          </>
        )}

        {/* ===== INTRO ===== */}
        {phase === "intro" && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-[2.5%] px-[6%] text-center">
            <img src={`${ASSET}/title.png`} alt="Giải cứu đại dương" className="w-[36%] drop-shadow-lg" />
            <div className="rounded-2xl bg-white/95 px-[4%] py-[2.5%] shadow-lg">
              <p className="text-h6 font-extrabold text-[#0e3a55]">Bản chơi thử — Giải cứu đại dương</p>
              <p className="mt-1 text-body-sm text-muted">Lớp mẫu 10 học sinh · 3 câu hỏi · chọn ngẫu nhiên bạn trả lời</p>
            </div>
            <button type="button" onClick={start} className="orx-btn orx-btn-primary">
              Bắt đầu chơi thử
            </button>
          </div>
        )}

        {/* ===== PLAYING ===== */}
        {phase === "playing" && (
          <div className="absolute inset-0 z-10 flex">
            <div className="relative flex flex-1 flex-col p-[2%]">
              {sub === "pick" && (
                <div className="flex flex-1 flex-col items-center justify-center gap-[3cqh]">
                  <span className="orx-pill orx-title-pill">Đã giải cứu {answeredCount}/{TOTAL}</span>
                  {(picking || current) && highlightStudent ? (
                    <div className="flex flex-col items-center gap-[1.5cqh]">
                      <span
                        className={cn("grid place-items-center overflow-hidden rounded-full bg-white/90 shadow-lg ring-4", picking ? "ring-sky-300" : "ring-amber-300")}
                        style={{ width: "22cqh", height: "22cqh" }}
                      >
                        <MascotAny value={highlightStudent.mascot} />
                      </span>
                      <span className="orx-feedback">{highlightStudent.fullName}</span>
                      {current && !picking && <span className="orx-pill orx-title-pill">Mời em lên trả lời! 🎉</span>}
                    </div>
                  ) : (
                    <button type="button" onClick={drawStudent} className="orx-btn orx-btn-primary inline-flex items-center gap-2">
                      <Dices className="size-[1.2em]" aria-hidden /> Chọn bạn ngẫu nhiên
                    </button>
                  )}
                </div>
              )}

              {sub === "choose" && (
                <div className="flex flex-1 flex-col">
                  <div className="mb-[1%] flex flex-col items-center gap-[1cqh]">
                    <span className="orx-pill">Lượt của {turnLabel} — chọn 1 loại rác để giải cứu</span>
                    <button type="button" onClick={drawStudent} disabled={picking} className="inline-flex items-center gap-1 rounded-full bg-white/90 px-3 py-1 text-caption font-bold text-[#0e3a55] shadow hover:bg-white disabled:opacity-60">
                      <Dices className="size-3.5" aria-hidden /> Đổi bạn khác
                    </button>
                  </div>
                  <div className="flex flex-1 flex-wrap content-center items-center justify-center gap-[2%]">
                    {DEMO_QUESTIONS.map((_, i) => {
                      const oc = outcomes[i];
                      const handled = oc !== undefined;
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => chooseTrash(i)}
                          disabled={handled}
                          className={cn("orx-trash relative", handled && (oc === "saved" ? "orx-trash-saved" : "orx-trash-polluted"))}
                          style={{ width: "13%", height: "21cqh" }}
                        >
                          <img src={`${ASSET}/trash/${(i % 8) + 1}.png`} alt="" className="h-full w-full object-contain" aria-hidden />
                          {oc === "saved" && <span className="orx-badge orx-badge-ok">✓</span>}
                          {oc === "polluted" && <span className="orx-badge orx-badge-bad">✕</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {sub === "question" && qIndex !== null && DEMO_QUESTIONS[qIndex] && (
                <QuestionView q={DEMO_QUESTIONS[qIndex]!} chosen={chosen} turnLabel={turnLabel} onMark={mark} />
              )}
            </div>
          </div>
        )}

        {/* ===== END ===== */}
        {phase === "end" && <EndBoard rows={rows} onReplay={start} />}
      </div>
    </div>
  );
}

function QuestionView({ q, chosen, turnLabel, onMark }: { q: PlayQuestionDTO; chosen: number | null; turnLabel: string; onMark: (i: number) => void }) {
  return (
    <div className="relative flex h-full flex-1 flex-col gap-[1.5cqh] py-[0.5cqh]">
      <div className="shrink-0 text-center">
        <span className="orx-pill">Lượt của {turnLabel} — bấm chọn đáp án</span>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <div className="relative inline-flex max-w-full">
          <img src={`${ASSET}/q-frame.png`} alt="" className="object-contain" style={{ maxHeight: "42cqh", maxWidth: "62cqw" }} aria-hidden />
          <div className="orx-qtext absolute" style={{ top: "40%", bottom: "10%", left: "9%", right: "9%" }}>
            <FitText text={q.prompt} maxPx={64} minPx={13} />
          </div>
        </div>
      </div>
      <div className="mx-auto grid w-full max-w-[62cqw] shrink-0 grid-cols-2 gap-x-[1.6cqw] gap-y-[1.4cqh]">
        {ANSWERS.map((a, i) => {
          const opt = q.options[i];
          if (opt === undefined) return <span key={i} aria-hidden />;
          const reveal = chosen !== null;
          const isCorrect = i === q.correctIndex;
          const picked = chosen === i;
          return (
            <button
              key={i}
              type="button"
              onClick={() => onMark(i)}
              disabled={reveal}
              style={{ height: "13cqh" }}
              className={cn("orx-answer relative", reveal && isCorrect && "orx-answer-correct", reveal && picked && !isCorrect && "orx-answer-wrong", reveal && !isCorrect && !picked && "opacity-60")}
            >
              <img src={a.img} alt="" className="h-full w-full object-fill" aria-hidden />
              <div className="orx-atext absolute" style={{ top: "17%", bottom: "12%", left: "14%", right: "7%" }}>
                <FitText text={opt} maxPx={34} minPx={11} />
              </div>
              {reveal && isCorrect && <Check className="absolute right-[5%] top-1/2 size-[1.3em] -translate-y-1/2 text-emerald-600" aria-hidden />}
              {reveal && picked && !isCorrect && <X className="absolute right-[5%] top-1/2 size-[1.3em] -translate-y-1/2 text-rose-600" aria-hidden />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EndBoard({ rows, onReplay }: { rows: { id: string; label: string; mascot: string; score: number }[]; onReplay: () => void }) {
  const medals = ["🥇", "🥈", "🥉"];
  return (
    <div className="absolute inset-0 z-10 overflow-y-auto">
      <div className="relative z-10 mx-auto flex min-h-full max-w-3xl flex-col items-center px-[4%] py-[3%]">
        <div className="flex items-center gap-2 rounded-full bg-white/95 px-[3%] py-[1.2%] shadow">
          <Trophy className="size-[1.4em] text-amber-500" aria-hidden />
          <span className="text-h6 font-extrabold text-[#0e3a55]">Kết quả — Giải cứu đại dương</span>
        </div>

        {rows.length > 0 ? (
          <div className="mt-[2%] w-full space-y-1.5">
            {rows.map((r, i) => (
              <div key={r.id} className="flex items-center gap-3 rounded-xl bg-white/95 px-3 py-2 shadow-sm">
                <span className="w-7 text-center text-body font-extrabold text-[#0e3a55]">{medals[i] ?? i + 1}</span>
                <span className="size-8 shrink-0 overflow-hidden rounded-full ring-1 ring-black/10"><MascotAny value={r.mascot} className="text-lg" /></span>
                <span className="min-w-0 flex-1 truncate text-body-sm font-semibold text-foreground">{r.label}</span>
                <span className="flex items-center gap-1 text-body-sm font-extrabold text-amber-600">{r.score} <Star className="size-4 fill-amber-400 text-amber-400" aria-hidden /></span>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-[2%] rounded-full bg-white/85 px-3 py-1.5 text-caption text-muted">Chưa có bạn nào tham gia.</p>
        )}

        {/* CTA đăng ký (thay cho cộng sao ở bản thật) */}
        <div className="mt-[3%] w-full max-w-xl rounded-2xl bg-white/95 p-[3%] text-center shadow">
          <p className="flex items-center justify-center gap-1.5 text-body font-bold text-[#0e3a55]">
            <Sparkles className="size-4 text-amber-500" aria-hidden /> Thích trò chơi này?
          </p>
          <p className="mt-1 text-body-sm text-muted">
            Đăng ký miễn phí để tạo bộ câu hỏi của riêng bạn, chơi cùng cả lớp và cộng sao thi đua cho học sinh.
          </p>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
            <Link href="/dang-ky" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2.5 text-body-sm font-bold text-white hover:bg-primary/90">
              Đăng ký miễn phí
            </Link>
            <button type="button" onClick={onReplay} className="inline-flex items-center gap-1.5 rounded-full border-2 border-border-strong bg-surface px-5 py-2.5 text-body-sm font-bold text-foreground hover:border-primary">
              <RotateCcw className="size-4" aria-hidden /> Chơi lại
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
